import { Typography, ButtonBase } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { KpiValue } from '@teamsystem-impact/shared';
import type { KpiDefinition } from '../kpiConfig';

interface KpiCardProps {
  definition: KpiDefinition;
  value: KpiValue | null;
  onClick: (anchorEl: HTMLElement) => void;
}

const COLOR_MAP: Record<string, string> = {
  default: '#1a1a1a',
  success: '#2e7d32',
  danger: '#c62828',
};

function formatValue(value: KpiValue | null, definition: KpiDefinition, locale: string): string {
  if (!value) return '—';
  if (value.currentValue !== null && value.currentValue !== undefined) {
    const scaled = value.currentValue * (definition.scale ?? 1);
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: definition.maxFractionDigits ?? 2,
    }).format(scaled);
  }
  if (value.textValue) return value.textValue;
  return '—';
}

export function KpiCard({ definition, value, onClick }: KpiCardProps) {
  const { t, i18n } = useTranslation();
  const color = COLOR_MAP[definition.valueColor || 'default'] ?? COLOR_MAP['default'];
  const hasValue = value && (value.currentValue !== null || value.textValue);
  const locale = i18n.language === 'en' ? 'en-GB' : 'it-IT';
  const label = definition.labelKey ? t(definition.labelKey, { defaultValue: definition.label }) : definition.label;
  const caption = definition.captionKey ? t(definition.captionKey, { defaultValue: definition.caption ?? '' }) : definition.caption;

  return (
    <ButtonBase
      onClick={(e) => onClick(e.currentTarget)}
      sx={{
        width: '100%',
        textAlign: 'center',
        p: 2,
        borderRight: '1px solid #f0f0f0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        transition: 'background 0.15s',
        '&:hover': { bgcolor: '#f0f7ff' },
        '&:last-of-type': { borderRight: 'none' },
      }}
    >
      <Typography
        variant="h4"
        sx={{ fontWeight: 700, color: hasValue ? color : '#bdbdbd', lineHeight: 1 }}
      >
        {formatValue(value, definition, locale)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {definition.unit}
      </Typography>
      <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 500, mt: 0.5 }}>
        {label}
      </Typography>
      {caption && (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontSize: 10.5, lineHeight: 1.3, mt: 0.25, fontStyle: 'italic' }}
        >
          {caption}
        </Typography>
      )}
    </ButtonBase>
  );
}
